import {
  AfterViewInit, ChangeDetectorRef, Component, ElementRef, Input, OnChanges,
  OnDestroy, OnInit, ViewChild
} from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {dayjs} from '@karve.it/core';
import { TagsService } from '@karve.it/features';
import { ListTagsOutput, Tag, TAG_TYPES } from '@karve.it/interfaces/tag';
import { createSelector, Store } from '@ngrx/store';
import { QueryRef } from 'apollo-angular';

import { SelectItem } from 'primeng/api';
import { merge } from 'rxjs';
import { concatMap, debounceTime, filter, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

import { Job, JobPageListJobsQueryVariables, JobsFilter, JobsPageCountGQL, JobsPageGQL, JobsPageQuery, JobsPageQueryVariables, JobWithUsersAndTagsFragment, ObjectCalendarEventsFilterInput, ZoneDir } from '../../generated/graphql.generated';

import { eventTypeInfoMap, JOB_STAGES, JOB_STATES, pagination } from '../global.constants';
import { strToTitleCase } from '../js';
import { convertDollarsToCents } from '../lib.ts/currency.util';
import { DetailsHelperService } from '../services/details-helper.service';
import { FreyaHelperService } from '../services/freya-helper.service';
import { ResponsiveHelperService } from '../services/responsive-helper.service';
import { JobParams, ParamTypes, QueryParamsService } from '../shared/query-params.service';
import { createConfigSelector } from '../state/branding.store';
import { DataError, parseGraphqlErrors } from '../utilities/errors.util';
import { WatchQueryHelper } from '../utilities/watchQueryHelper';

export interface JobWithError extends JobWithUsersAndTagsFragment {
  error: DataError;
}

export interface JobClosureReason {
  id: string;
  title: string;
  stages: string[];
}

type DateRangeSelection = [ Date | undefined, Date | undefined ];


export const selectJobClosedReasonList = createSelector(
  createConfigSelector('JOBS_CLOSEDREASONS'),
  (reasons) => [
    {
      option: undefined,
      label: 'Any',
    },
    ...reasons.map((cr) => ({
      option: cr.id,
      label: cr.title,
    })),
  ],
);


@Component({
  selector: 'app-jobs',
  templateUrl: './jobs.component.html',
  styleUrls: ['./jobs.component.scss']
})
export class JobsComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {

  @ViewChild('searchInput', { static: false }) searchInput!: ElementRef;

  // Filter Variables
  @Input() filter: JobsFilter = {};
  @Input() pagination = pagination;

  /**
   * Tag names to filter by, delimited the same way as the `tags` query param
   * (e.g. `'approval required'`, or `'invoice-open;lead-new'`).
   *
   * Requirement: a caller that knows a tag only by name must still be able to
   * filter by it. Tag ids differ per zone, so callers fixed at build time -- a
   * route, say -- cannot pass one through {@link filter}, which accepts only
   * tagIds. Names are resolved against the loaded tag list in retrieveTags().
   *
   * Both callers pass {@link syncToQueryParams} false, which is the only
   * configuration this is consulted in. Were a caller to enable it, updateForm()
   * would rebuild the form from the URL and reassign passedTags, overwriting
   * this -- so the two would need reconciling before that combination is used.
   */
  @Input() tagNames: string;

  /**
   * When true, makes sure the search form values remain synced to any relevant query params,
   * i.e., when the form values change (e.g., user types 'Toledo' into the search box),
   * it updates the query params ('search=Toledo' is added to the URL), and vice versa
   *
   * Turn it off when embedding this into another component
   */
  @Input() syncToQueryParams = true;

  // Modify Component Layout
  @Input() tableTitle: string = undefined;
  @Input() showHeaderCard = true;
  @Input() activeData = {
    stage: true,
    tags: true,
    state: true,
    status: false,
    code: true,
    customer: true,
    total: true,
    amountDue: false,
    date: true,
    firstBookedRevenueGeneratingEvent: true,
    timeline: true,
    link: true,
  };

  subs = new SubSink();

  stages = [
    { option: 'any', label: 'Any' },
    ...Object.values(JOB_STAGES).map((s) => ({
      option: s.name,
      label: strToTitleCase(s.name)
    }))
  ];

  states = [
    { option: 'any', label: 'Any' },
    ...JOB_STATES.map((s) => ({
      option: s,
      label: strToTitleCase(s),
    }))
  ];

  closedReasons$ = this.store.select(selectJobClosedReasonList);
  defaultCreatedAtFilter$ = this.store.select(createConfigSelector('JOBS_DEFAULTCREATEDATFILTER'));

  tags: Tag[] = [];
  passedTags: string;

  // Component Variables
  jobs: JobWithError[] = [];

  // Job Query Variables
  jobsQueryRef!: QueryRef<JobsPageQuery, JobPageListJobsQueryVariables>;
  jobsQH: WatchQueryHelper = {
    limit: this.getPageSize(),
    skip: 0,
    loading: true,
    hasMore: true,
    total: undefined,
    mergeNextResults: false,
    isFirstLoad: true,
  };

  jobCountLoading = false;

  jobPlaceholderCount = Array.from({ length: this.jobsQH.limit });

  jobPlaceholderColumnCount = Array.from({ length: 0 });

  // An array of the indexes which contain errors
  jobDataErrors: DataError[] = [];

  jobSearchForm = new FormGroup({
    search: new FormControl(''),
    stage: new FormControl<string>('any'),
    state: new FormControl<string>('any'),
    tagIds: new FormControl<string[]>([]),
    created: new FormControl<DateRangeSelection>(undefined),
    lastUpdated: new FormControl<DateRangeSelection>(undefined),
    bookingDates: new FormControl<DateRangeSelection>(undefined),
    closedAt: new FormControl<DateRangeSelection>(undefined),
    bookedEvents: new FormControl<any>(undefined),
    transactionMin: new FormControl<number>(undefined),
    transactionMax: new FormControl<number>(undefined),
    closedReason: new FormControl<string>(undefined),
    archivedAt: new FormControl<DateRangeSelection>(undefined),
    sources: new FormControl<string[]>([]),
  });

  jobSearchFormDefaults = this.jobSearchForm.getRawValue();

  // Tag Query Variables
  tagQueryRef: QueryRef<ListTagsOutput>;

  // Filter Variables
  filtersCollapsed = true;

  eventTypes: SelectItem[] = Object.values(eventTypeInfoMap)
    .map((et) => ({
      value: et.value,
      label: et.name
    }));

  currencyFilters = {
    currency: 'USD',
    locale: 'en-US'
  };


  paramsChanged$ = this.route.queryParams
    .pipe(
      filter(params => 'zone' in params),
      debounceTime(10),
    );

  constructor(
    private detailsHelper: DetailsHelperService,
    private jobsListGQL: JobsPageGQL,
    private route: ActivatedRoute,
    private ref: ChangeDetectorRef,
    private tagService: TagsService,
    public responsiveHelper: ResponsiveHelperService,
    private router: Router,
    private queryParams: QueryParamsService,
    public freyaHelper: FreyaHelperService,
    private jobsPageCountGQL: JobsPageCountGQL,
    private elementRef: ElementRef,
    private store: Store,
    ) { }

  ngOnInit(): void {

    this.primeJobSearchForm();

    this.jobs = [];

    this.watchObjectUpdates();
    this.watchJobSearchFormValueChanges();

    this.watchQueryParams();
    this.setJobPlaceholderColumnCount();

    this.subscribeToDefaultCreatedAtFilter();
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }

  ngAfterViewInit() {
    this.ref.detectChanges();
  }

  ngOnChanges() {
    // Lock the page size once the first request is out: retrieveMoreJobs()
    // advances skip by limit, so changing it mid-list would skip rows.
    if (this.jobsQueryRef) { return; }
    this.jobsQH.limit = this.getPageSize();
    this.jobPlaceholderCount = Array.from({ length: this.jobsQH.limit });
  }

  /**
   * Page size large enough to fill the viewport in one request. With a fixed
   * page of 10, tall screens show the lazy-load sentinel immediately, which
   * chains a second request and leaves the bottom rows as skeletons meanwhile.
   */
  getPageSize(): number {
    const estimatedRowHeightPx = 100;
    const bufferRows = 2;
    const rowsToFillViewport = Math.ceil(window.innerHeight / estimatedRowHeightPx) + bufferRows;
    return Math.max(this.pagination.defaultNumberOfItems, rowsToFillViewport);
  }

  generateLink(jobId: string): any[] {
    return ['/jobs', jobId];
  }

  openReport(): void {
    const filter = this.generateJobsFilter();
    const queryParams: Record<string, string> = {};
    if (filter.search) { queryParams.search = filter.search; }
    if (filter.stage) { queryParams.stage = filter.stage; }
    if (filter.getJobsOfState?.length) { queryParams.state = filter.getJobsOfState[0]; }
    const url = this.router.serializeUrl(
      this.router.createUrlTree(['/reports', 'dynamic', 'new'], { queryParams }),
    );
    window.open(url, '_blank');
  }

  /**
   * Keeps loaded jobs in sync with successful job and user mutations.
   */
  watchObjectUpdates() {
    this.subs.sink = this.detailsHelper.getObjectUpdates(['Jobs', 'User']).subscribe((update) => {
      if (update.type === 'Jobs') {
        this.refreshJobsByIds([update.id]);
        return;
      }

      if (update.type === 'User') {
        this.refreshJobsByUserId(update.id);
      }
    });
  }

  /**
   * Refetches jobs that reference a specific user and replaces only those rows locally.
   */
  private refreshJobsByUserId(userId: string) {
    if (!userId) { return; }

    const relatedJobIds = this.jobs
      .filter((job) => job.users?.some((jobUser) => jobUser?.user?.id === userId))
      .map((job) => job.id);

    this.refreshJobsByIds(relatedJobIds);
  }

  /**
   * Refetches specific jobs and replaces them in the local infinite-scroll list.
   */
  private refreshJobsByIds(jobIds: string[]) {
    if (!this.jobsQueryRef) { return; }
    if (!jobIds?.length) { return; }

    const uniqueJobIds = [...new Set(jobIds.filter(Boolean))];
    const jobsToRefresh = new Set(uniqueJobIds.filter((jobId) => this.jobs.some((job) => job.id === jobId)));
    if (!jobsToRefresh.size) { return; }

    const variables: JobsPageQueryVariables = {
      ...this.generateWatchJobsInput(),
      skip: 0,
      limit: jobsToRefresh.size,
      filter: {
        ...this.generateJobsFilter(),
        jobIds: [...jobsToRefresh],
      },
    };

    this.subs.sink = this.jobsListGQL.fetch(
      variables,
      {
        fetchPolicy: 'network-only',
        errorPolicy: 'all',
      },
    ).subscribe((res) => {
      if (res.loading) { return; };
      const fetchedJobs = res.data?.jobs?.jobs || [];
      const fetchedJobErrors = parseGraphqlErrors(res.errors, 'jobs', 2);

      const updatedJobsById = new Map<string, JobWithError>(
        fetchedJobs.map((fetchedJob, index) => {
          const jobError = fetchedJobErrors.find((err) => err.listIndex === index);
          return [
            fetchedJob.id,
            {
              ...fetchedJob,
              error: jobError,
            },
          ];
        }),
      );

      // If a job is missing from the result, it no longer matches current filters (or was deleted),
      // so remove it from the local list.
      this.jobs = this.jobs.flatMap((existingJob) => {
        if (!jobsToRefresh.has(existingJob.id)) {
          return [existingJob];
        }

        const refreshedJob = updatedJobsById.get(existingJob.id);
        return refreshedJob ? [refreshedJob] : [];
      });
    });
  }

  /**
   * Executes a different action every time the form is updated,
   * depending on the value of {@link syncToQueryParams}
   *
   * If {@link syncToQueryParams} is set to true, it will update the query params,
   * which should in turn prompt the component to refetch jobs.
   *
   * If false, it will refetch jobs *directly*,
   * without updating the query params.
   */
  watchJobSearchFormValueChanges() {

    /**
     * generate an array of observables for each
     * form control. We can't use jobSearchForm.valueChanges
     * because we need to modify behaviour for
     * search and state.
     */
    const valueChanges = Object.keys(this.jobSearchForm.controls)
      .map((key) => {

        let time = 250;
        if (key === 'search') {
          time = 750;
        }
        const modifiers = [];
        if (key === 'state') {
          modifiers.push(tap((state: any) => this.toggleStateRelatedControls(state, false)));
        }

        return this.jobSearchForm.controls[key].valueChanges.pipe(
          ...modifiers,
          debounceTime(time)
        )
      });


    this.subs.sink = merge(...valueChanges).pipe(
      debounceTime(10),
    ).subscribe(() => {
      if (this.syncToQueryParams) {
        this.updateQueryParams();
      } else {
        this.jobsQH.mergeNextResults = false;
        this.jobsQH.skip = 0;
        this.jobsQH.total = undefined;
        this.retrieveJobs();
      }
    });
  }

  /**
   * Formats the values of an Angular form so they can be embedded in the URL as query params.
   *
   * @param formValues An object containing the values of an Angular formGrop.
   * @returns The *same* object, stripped of any empty values,
   * and with the remaining values converted into a URL-friendly format.
   */
  formatSearchFormValues(formValues: AbstractControl['value']): JobParams {

    // Tell the quer params service how you want each control formatted
    const customControls = {
      dateRange: { controls: [ 'created', 'lastUpdated', 'bookingDates', 'closedAt', 'archivedAt'] },
      tagArray: { controls: ['tagIds'], tags: this.tags }
    };

    return this.queryParams.formatSearchFormValues(formValues, customControls);
  }

  /**
   * Embeds the current values of the search form into the URL as query params
   *
   * Will then call "watchQueryParams" if "syncToQueryParams" is true
   */
  updateQueryParams() {
    this.router.navigate([], {
      queryParams: this.formatSearchFormValues(this.jobSearchForm.value)
    });
    // After navigate, see "watchQueryParams" below
  }

  /**
   * Updates the search form and executes a new search query every time the
   * query params change.
   */
  watchQueryParams() {
    if (!this.syncToQueryParams) { return; }

    this.subs.sink = this.paramsChanged$
    .subscribe(async (params) => {

      const { zone: _zone, ...filterParams } = params;

      this.queryParams.stripEmptyParams(filterParams);

      this.searchWithFilter(filterParams);
    });
  }

  /**
   * Updates the search with the values from a given `jobFilter` and executes a new job search query with said values.
   *
   * @param jobFilter The query params extracted from the current URL.
   */
  searchWithFilter(jobFilter: JobParams) {
    this.jobsQH.mergeNextResults = false;
    this.jobsQH.skip = 0;
    this.jobsQH.total = undefined;
    this.updateForm(jobFilter);
    this.retrieveJobs();
  }

  /**
   * Updates the search form *silently*,
   * (i.e., without making the form controls' `valueChanges` observable fire,
   * which is important to avoid an infinite loop).
   *
   * @param jobParams The query params extracted from the current URL
   */
  updateForm(jobParams: JobParams) {
    // Tags param must be handled differently as we need to wait for tags to load before updating the form
    const { tags, ...otherParams } = jobParams;

    // Tell the queryParams service how we want to parse each param
    const paramTypes: ParamTypes = {
      num: { params: ['transactionMin', 'transacionMax'] },
      dateArr: { params: ['created', 'lastUpdated', 'bookingDates', 'closedAt', 'archivedAt'] },
      arr: { params: ['bookedEvents', 'sources' ] },
    };

    const updatedFormValue = {
      ...this.jobSearchFormDefaults,
      ...this.queryParams.parseQueryParams(otherParams, paramTypes),
    };

    // this.jobSearchForm.
    this.jobSearchForm.setValue(updatedFormValue, {
      emitEvent: false,
    });

    this.toggleStateRelatedControls(this.jobSearchForm.controls.state.value, false);

    // Handle tags
    if (!this.tagQueryRef) {
      this.passedTags = tags;
    } else {
      this.passedTags =  undefined;
      const tagIds = this.queryParams.parseTagQueryParam(tags, this.tags);
      this.jobSearchForm.controls.tagIds.setValue(tagIds, { emitEvent: false });
    }

    this.setFormStatus();
  }

  toggleStateRelatedControls(state: string, emitEvent: boolean) {
    const stateRelatedControlNames = [ 'closedAt', 'closedReason' ];

    const queryIncludesClosedJobs = !state || state === 'closed';

    for (const controlName of stateRelatedControlNames) {
      const control = this.jobSearchForm.get(controlName);

      if (queryIncludesClosedJobs) {
        control.enable({ emitEvent });
      } else {
        control.setValue(undefined, { emitEvent });
        control.disable({ emitEvent });
      };
    }
  }

  /**
   * Sets the search form status as pristine or dirty depending on its value
   */
  setFormStatus() {
    const formValues = Object.values(this.jobSearchForm.value)
      .filter(Boolean)
      .filter(val => {
        if (!(Array.isArray(val) || typeof val === 'string')) { return true; }
        return val.length;
      });

    if (!formValues.length) {
      this.jobSearchForm.markAsPristine();
    } else {
      this.jobSearchForm.markAsDirty();
    }
  }

  // Search For Jobs based on filter values
  async retrieveJobs() {
    // this.jobsQH.loading = true;
    if (!this.tagQueryRef) {
      await this.retrieveTags();
    }

    if (this.jobsQueryRef) {
      const variables = this.generateWatchJobsInput();
      return this.jobsQueryRef.setVariables(variables);
    };

    this.initJobsListWatchQuery();
  }

  initJobsListWatchQuery() {
    this.jobsQueryRef = this.jobsListGQL.watch(this.generateWatchJobsInput(), {
      fetchPolicy: 'cache-and-network',
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
      returnPartialData: true,

    });

    this.subs.sink = this.jobsQueryRef.valueChanges.subscribe((res) => {
      this.jobsQH.loading = res.loading;
      if (res.loading) { return; };
      if (!res.data?.jobs?.jobs) {
        console.warn('no jobs returned', res);
        return;
      };

      // Get Parse the Errors into DataError format
      this.jobDataErrors = parseGraphqlErrors(res.errors, 'jobs', 2);

      const jobsWithError = res.data.jobs.jobs
        .map((job, index) => (
          {
            ...job,
            error: this.jobDataErrors.find((err) => err.listIndex === index)}
        )
      ) || [];

      if (this.jobsQH.mergeNextResults) {
        this.jobs = [
          ...this.jobs,
          ...jobsWithError,
        ];
      } else {
        this.jobs = jobsWithError;
      }

      this.jobsQH.hasMore = res.data.jobs.jobs.length === this.jobsQH.limit;
      this.jobsQH.isFirstLoad = false;
    });
  }

  generateWatchJobsInput(): JobsPageQueryVariables {
    const jobsFilter = this.generateJobsFilter();

    const { getJobsOfState } = jobsFilter;

    const fetchClosedJobs = getJobsOfState?.includes('closed');

    const watchJobsInput: JobsPageQueryVariables = {
      limit: this.jobsQH.limit,
      skip: this.jobsQH.skip,
      filter: jobsFilter,
      resolve: ['users', 'firstBookedRevenueGenerating'],
      sort: fetchClosedJobs ? 'closedAt:desc' : 'createdAt:desc',
    };

    return watchJobsInput;
  }

  generateJobsFilter(): JobsPageQueryVariables['filter'] {
    const val = this.jobSearchForm.value;

    const transactionMin = val.transactionMin ? convertDollarsToCents(val.transactionMin) : undefined;
    const transactionMax= val.transactionMax ? convertDollarsToCents(val.transactionMax) : undefined;

    const getDeleted = !val.stage || val.stage === 'deleted';

    return {
      ...this.filter,
      tagIds: val.tagIds?.length ? val.tagIds : undefined,
      search: val.search,
      stage: val.stage === 'any' ? undefined : val.stage,
      zoneDir: ZoneDir.Lte,
      created: this.queryParams.convertDatesToNumValInput(val.created),
      lastUpdated: this.queryParams.convertDatesToNumValInput(val.lastUpdated),
      closedAt: this.queryParams.convertDatesToNumValInput(val.closedAt),
      archived: this.queryParams.convertDatesToNumValInput(val.archivedAt),
      eventsFilter: this.generateEventsFilter(val.bookedEvents, val.bookingDates),
      transactionAmount: this.queryParams.getNumValInput(transactionMin, transactionMax),
      closedReason: val.closedReason,
      getJobsOfState: this.getStateFilter(val.state),
      sources: val.sources?.length ? val.sources : undefined,
      getDeleted,
    };
  }

  /**
   * Resolves the state filter from the single-select form control, falling back to the
   * states passed through {@link filter}.
   *
   * Requirement: a multi-state `filter` input must reach the query intact. The control
   * holds one state, so it cannot represent more than one -- priming it from such a
   * filter would collapse `['open', 'closed']` to `['open']` and silently drop jobs.
   * Those inputs are therefore left out of the form, which stays on 'any', and passed
   * through here instead. Picking a concrete state still narrows them, and for a
   * single-state input 'any' still means any, as it does for stage and the rest.
   */
  private getStateFilter(state: string): JobsFilter['getJobsOfState'] {
    if (state && state !== 'any') { return [ state ]; }

    const passedStates = this.filter.getJobsOfState;

    return passedStates?.length > 1 ? passedStates : undefined;
  }

  /**
   * Generates an object of type ObjectCalendarEventsFilterInput
   * used by the backend to filter jobs by their associated events.
   *
   * Requested jobs should have at least one event of any of the provided event types
   * booked within the specified range of dates.
   *
   * @param events An array of event types such that the requested jobs should be related to an event of any such type.
   * @param dates The range of dates where the associated events should have been booked.
   * @returns An object of type ObjectCalendarEventsFilterInput.
   */
  generateEventsFilter(events: string[], dates: Date[]): ObjectCalendarEventsFilterInput {
    if (!events || !dates) { return; };
    const [ startDate, endDate ] = dates;
    const calendarEventsFilter: ObjectCalendarEventsFilterInput = {
      types: events,
      start: this.queryParams.convertDatesToNumValInput([ startDate ]),
      end: this.queryParams.convertDatesToNumValInput([ undefined, endDate])
    };
    return calendarEventsFilter;
  }

  /**
   * Resets the search form and searches for jobs with empty filters.
   *
   * Prevents Angular FormGroup default behavior, which is to emit
   * value changes from each control on reset. Component listens
   * to control value changes and refetches every time they emit,
   * so the default behavior would result in many unnecessary calls.
   */
  resetFilters() {
    this.jobSearchForm.reset(undefined, { emitEvent: false });
    if (this.syncToQueryParams) {
      this.updateQueryParams();
    } else {
      this.jobsQH.mergeNextResults = false;
      this.jobsQH.skip = 0;
      this.jobsQH.total = undefined;
      this.retrieveJobs();
    }
    this.jobSearchForm.markAsPristine();
  }

  formatTimeline(timeline: string) {
    return dayjs(timeline).toDate();
  }

  formatTimelineTooltip(created: number, strTimeline: string) {
    if (!created) {
      return `Job not created.`;
    }

    if (!strTimeline) {
      return `Timeline not set.`;
    }
    const mCreated = dayjs(created * 1000);
    const mTimeline = dayjs(strTimeline);
    let diffMethod: dayjs.QUnitType | dayjs.OpUnitType = 'days';
    let diff = mTimeline.diff(mCreated, diffMethod);
    if (diff > 21) {
      diffMethod = 'weeks';
      diff = mTimeline.diff(mCreated, diffMethod);
      if (diff > 3) {
        diffMethod = 'months';
        diff = mTimeline.diff(mCreated, diffMethod);
      }
    }

    return `${ diff } ${ diffMethod } out`;
  }

  /**
   * Prefills jobSearchForm with any relevant data
   * passed through the filter and tagNames inputs.
   */
  primeJobSearchForm() {
    const { search, stage, tagIds, getJobsOfState } = this.filter;

    // Requirement: a caller's `filter` must survive into the query. generateJobsFilter()
    // rebuilds the query from the form, so every key the form also owns has to be primed
    // here or the form's default overwrites it. State is easy to miss as a pair, since
    // the control is `state` while the filter key is `getJobsOfState`: unprimed, a route
    // passing getJobsOfState: ['closed'] emitted 'any' and listed the open tab's jobs.
    this.jobSearchForm.patchValue({
      search: search || '',
      stage: stage || 'any',
      tagIds: tagIds || [],
      state: getJobsOfState?.length === 1 ? getJobsOfState[0] : 'any',
    });

    this.toggleStateRelatedControls(this.jobSearchForm.controls.state.value, false);

    // Names cannot be resolved to ids until the tag list loads, so hand them to
    // retrieveTags() -- which retrieveJobs() awaits before building the query
    // variables, so the ids do make it into the first request.
    if (this.tagNames) {
      this.passedTags = this.tagNames;
    }
  }

  // Pagination
  retrieveMoreJobs() {
    if (this.jobsQH.loading || !this.jobsQH.hasMore) { return; }
    this.jobsQH.skip += this.jobsQH.limit;
    this.jobsQH.mergeNextResults = true;
    this.retrieveJobs();
  }

  selectJob(job: Job) {
    this.detailsHelper.detailsItem.next({ type: 'job', item: {id: job.id} });
  }

  async retrieveTags() {
    return new Promise((resolve, reject) => {
      this.tagQueryRef = this.tagService.watchTags(
        {
          filter: {
            objectTypes: [TAG_TYPES.Job]
          }
        },
        {
          fetchPolicy: 'cache-and-network'
        }
      );

      this.subs.sink = this.tagQueryRef.valueChanges.subscribe({
        next: (res) => {
          if (res.loading) { return; }
          this.tags = res.data.tags.tags;
          if (this.passedTags) { // If we have tags passed in to filter by then set the filter
            const tagIds = this.queryParams.parseTagQueryParam(this.passedTags, this.tags);
            if (!tagIds?.length) { return resolve(false); }
            this.jobSearchForm.controls.tagIds.setValue(tagIds, { emitEvent: false });
            this.setFormStatus();
          }
          resolve(true);
        },
        error: (err) => {
          reject(err);
        }
      });
    });
  }

  setJobPlaceholderColumnCount() {
    // Link column is a special case
    const length = Object.keys(this.activeData).filter((key) => this.activeData[key] && key !== 'link').length;
    this.jobPlaceholderColumnCount = Array.from({ length }) ;
  }

  getJobCount() {
    this.jobsPageCountGQL.fetch(this.generateWatchJobsInput(), { notifyOnNetworkStatusChange: true })
      .subscribe((res) => {
        this.jobCountLoading = res.loading;
        if (res.loading) { return; }
        this.jobsQH.total = res.data.jobs.total;
      });
  }
  /**
   * Subscribes to the defaultCreatedAtFilter$ observable,
   * which emits a value when the default created at filter is set.
   *
   * If the value is defined, it patches the jobSearchForm with it,
   * and if not syncing to query params, it fetches jobs directly.
   */
  private subscribeToDefaultCreatedAtFilter(): void {
    // Wait for params changed to avoid overriding existing params before they are read
    const defaultCreatedAt$ = this.syncToQueryParams
      ? this.paramsChanged$.pipe(
          take(1),
          concatMap(() => this.defaultCreatedAtFilter$),
        )
      : this.defaultCreatedAtFilter$;

    this.subs.sink = defaultCreatedAt$.subscribe((defaultCreatedAt) => {
      // Always patch the form with the created filter, even if it's undefined.
      // This ensures that any previous filter values are cleared, and the form state stays consistent,
      // especially when the user changes the zone from the left menu.
      this.jobSearchForm.patchValue(
        { created: [defaultCreatedAt ? new Date(defaultCreatedAt * 1000) : undefined, undefined] },
        { emitEvent: false },
      );

      // If not syncing to query params, fetch jobs directly
      if (!this.syncToQueryParams) {
        this.jobsQH.mergeNextResults = false;
        this.jobsQH.skip = 0;
        this.jobsQH.total = undefined;
        this.retrieveJobs();
      } else {
        this.updateQueryParams();
      }
    });
  }
}
